import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  Building2,
  Briefcase,
  Send,
  UserCheck,
  Award,
  Clock,
  Search,
  Filter,
  RefreshCw,
  Download,
  Eye,
  ChevronRight,
  ChevronDown,
  Calendar,
  Target,
  DollarSign,
  BarChart3,
  PieChart,
  Activity,
  Users,
  FileText,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Phone,
  Mail,
  MapPin,
  Star,
  TrendingDown,
  ArrowUpRight,
  X,
  Edit,
  UserCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import CustomerManagement from "./CustomerManagement";
import JobManagement from "./JobManagement";
import ClientSubmissionEditModal from "../components/ClientSubmissionEditModal";
import ContactFormModal from "../components/ContactFormModal";
import ContactsTab from "../components/ContactsTab";
import CompanyCreateForm from "../components/CompanyCreateForm";
import CompaniesTab from "../components/CompaniesTab";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const SalesSamDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showEditSubmissionModal, setShowEditSubmissionModal] = useState(false);

  // Contacts state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0);

  // Companies state
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companiesRefreshTrigger, setCompaniesRefreshTrigger] = useState(0);

  // Data states
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [candidates, setCandidates] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    client: "all",
    dateRange: "last_30_days",
    status: "all",
    recruiter: "all",
    priority: "all",
    searchTerm: "",
  });

  // Metrics
  const [salesMetrics, setSalesMetrics] = useState({
    totalActiveClients: 0,
    totalOpenReqs: 0,
    totalSubmissions: 0,
    totalInterviews: 0,
    totalOffers: 0,
    totalHires: 0,
    avgTimeToFill: 0,
    conversionRate: 0,
  });

  const [clientSummaries, setClientSummaries] = useState([]);

  // Mock Sales Sam data (in production, get from auth context)
  const currentSalesRep = {
    id: "490fcd28-049c-4564-8771-52835b432bcc",
    name: "Sales Sam",
    email: "sam@junotalent.com",
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    calculateMetrics();
  }, [clients, jobs, submissions]);

  // Clear search terms when switching tabs
  useEffect(() => {
    setFilters((prev) => ({ ...prev, searchTerm: "" }));
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchClients(),
        fetchJobs(),
        fetchSubmissions(),
        fetchCandidates(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers`);
      if (response.ok) {
        const data = await response.json();
        // Filter clients assigned to Sales Sam
        const samClients = (data.customers || []).filter(
          (c) =>
            c.account_manager === currentSalesRep.name ||
            c.sales_rep === currentSalesRep.name ||
            true // For demo, show all
        );
        setClients(samClients);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/jobs`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const jobs = Array.isArray(data) ? data : data.jobs || [];
        setJobs(jobs);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/submissions`);
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error("Error fetching submissions:", error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/candidates`);
      if (response.ok) {
        const data = await response.json();
        // API returns array directly
        const candidates = Array.isArray(data) ? data : data.candidates || [];
        setCandidates(candidates);
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
    }
  };

  const calculateMetrics = () => {
    // Active clients with open jobs
    const activeClientNames = new Set(
      jobs.filter((j) => j.status === "open").map((j) => j.company)
    );
    const totalActiveClients = activeClientNames.size;

    // Open requisitions
    const totalOpenReqs = jobs.filter((j) => j.status === "open").length;

    // Submissions metrics
    const totalSubmissions = submissions.length;
    const totalInterviews = submissions.filter((s) =>
      [
        "interview_scheduled",
        "interview_scheduled",
        "interview_scheduled",
      ].includes(s.current_status)
    ).length;
    const totalOffers = submissions.filter((s) =>
      ["offer_made", "offer_accepted"].includes(s.current_status)
    ).length;
    const totalHires = submissions.filter(
      (s) => s.current_status === "offer_accepted"
    ).length;

    // Conversion rate
    const conversionRate =
      totalSubmissions > 0
        ? ((totalOffers / totalSubmissions) * 100).toFixed(1)
        : 0;

    // Average time to fill (simplified)
    const closedJobs = jobs.filter(
      (j) => j.status === "closed" && j.closed_at && j.created_at
    );
    const avgTimeToFill =
      closedJobs.length > 0
        ? Math.round(
            closedJobs.reduce((acc, job) => {
              const days = Math.floor(
                (new Date(job.closed_at) - new Date(job.created_at)) /
                  (1000 * 60 * 60 * 24)
              );
              return acc + days;
            }, 0) / closedJobs.length
          )
        : 0;

    setSalesMetrics({
      totalActiveClients,
      totalOpenReqs,
      totalSubmissions,
      totalInterviews,
      totalOffers,
      totalHires,
      avgTimeToFill,
      conversionRate,
    });

    // Calculate client summaries
    calculateClientSummaries();
  };

  const calculateClientSummaries = () => {
    const summaries = clients.map((client) => {
      const clientName = client.company_name || client.client_name;

      // Jobs for this client
      const clientJobs = jobs.filter((j) => j.company === clientName);
      const openReqs = clientJobs.filter((j) => j.status === "open").length;

      // Submissions for this client's jobs
      const clientJobIds = clientJobs.map((j) => j.id);
      const clientSubmissions = submissions.filter((s) =>
        clientJobIds.includes(s.job_id)
      );

      const activeSubmissions = clientSubmissions.filter(
        (s) =>
          !["offer_accepted", "rejected", "internally_rejected"].includes(
            s.current_status
          )
      ).length;

      const interviews = clientSubmissions.filter((s) =>
        [
          "interview_scheduled",
          "interview_scheduled",
          "interview_scheduled",
        ].includes(s.current_status)
      ).length;

      const offers = clientSubmissions.filter((s) =>
        ["offer_made", "offer_accepted"].includes(s.current_status)
      ).length;

      const hires = clientSubmissions.filter(
        (s) => s.current_status === "offer_accepted"
      ).length;

      return {
        id: client.id,
        name: clientName,
        openReqs,
        activeSubmissions,
        interviews,
        offers,
        hires,
        accountManager: client.account_manager || currentSalesRep.name,
        lastUpdated: client.updated_at || client.created_at,
        industry: client.industry,
        status: client.status,
      };
    });

    setClientSummaries(summaries.sort((a, b) => b.openReqs - a.openReqs));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleContactCreated = () => {
    setShowContactModal(false);
    // Trigger refresh by updating the key
    setContactsRefreshTrigger((prev) => prev + 1);
  };

  const handleCompanyCreated = () => {
    setShowCompanyModal(false);
    setSelectedCompany(null);
    // Trigger refresh by updating the key
    setCompaniesRefreshTrigger((prev) => prev + 1);
  };

  // Overview Tab
  const OverviewTab = () => {
    const [selectedMetric, setSelectedMetric] = useState(null);

    // Get Sam's jobs (jobs that Sam created/manages)
    const samJobs = jobs.filter((job) => job.status === "open");

    // Filter data based on selected metric
    const getFilteredData = () => {
      switch (selectedMetric) {
        case "jobs":
          return samJobs;
        case "submissions":
          // Show submissions only for Sam's jobs
          const samJobIds = samJobs.map((j) => j.id);
          return submissions.filter(
            (s) =>
              samJobIds.includes(s.job_id) &&
              s.status !== "rejected" &&
              s.status !== "offer_accepted"
          );
        case "interviews":
          const samJobIds2 = samJobs.map((j) => j.id);
          return submissions.filter(
            (s) =>
              samJobIds2.includes(s.job_id) &&
              s.status === "interview_scheduled"
          );
        case "offers":
          const samJobIds3 = samJobs.map((j) => j.id);
          return submissions.filter(
            (s) =>
              samJobIds3.includes(s.job_id) &&
              (s.status === "offer_made" || s.status === "offer_extended")
          );
        case "onboards":
          const samJobIds4 = samJobs.map((j) => j.id);
          return submissions.filter(
            (s) =>
              samJobIds4.includes(s.job_id) &&
              (s.status === "offer_accepted" || s.status === "onboarding")
          );
        default:
          return [];
      }
    };

    const filteredData = getFilteredData();

    // Calculate metrics for Sam's jobs
    const samJobIds = samJobs.map((j) => j.id);
    const samSubmissions = submissions.filter((s) =>
      samJobIds.includes(s.job_id)
    );
    const activeSubmissions = samSubmissions.filter(
      (s) => s.status !== "rejected" && s.status !== "offer_accepted"
    ).length;
    const interviewCount = samSubmissions.filter(
      (s) => s.status === "interview_scheduled"
    ).length;
    const offersCount = samSubmissions.filter(
      (s) => s.status === "offer_made" || s.status === "offer_extended"
    ).length;
    const onboardingCount = samSubmissions.filter(
      (s) => s.status === "offer_accepted" || s.status === "onboarding"
    ).length;

    return (
      <div className="space-y-6">
        {/* Stats Cards - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div
            onClick={() => setSelectedMetric("jobs")}
            className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">
                  Open Requisitions
                </p>
                <p className="text-3xl font-bold mt-1">{samJobs.length}</p>
              </div>
              <Briefcase className="h-12 w-12 text-orange-200" />
            </div>
          </div>

          <div
            onClick={() => setSelectedMetric("submissions")}
            className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">
                  Active Submissions
                </p>
                <p className="text-3xl font-bold mt-1">{activeSubmissions}</p>
              </div>
              <Send className="h-12 w-12 text-purple-200" />
            </div>
          </div>

          <div
            onClick={() => setSelectedMetric("interviews")}
            className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm font-medium">
                  Current Interviews
                </p>
                <p className="text-3xl font-bold mt-1">{interviewCount}</p>
              </div>
              <Calendar className="h-12 w-12 text-indigo-200" />
            </div>
          </div>

          <div
            onClick={() => setSelectedMetric("offers")}
            className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">
                  Open Offers Made
                </p>
                <p className="text-3xl font-bold mt-1">{offersCount}</p>
              </div>
              <Award className="h-12 w-12 text-green-200" />
            </div>
          </div>

          <div
            onClick={() => setSelectedMetric("onboards")}
            className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white cursor-pointer hover:shadow-xl hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">
                  Open Onboarding
                </p>
                <p className="text-3xl font-bold mt-1">{onboardingCount}</p>
              </div>
              <UserCheck className="h-12 w-12 text-blue-200" />
            </div>
          </div>
        </div>

        {/* Data Table - Shows when metric is clicked */}
        {selectedMetric && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedMetric === "jobs" && "Open Requisitions"}
                {selectedMetric === "submissions" && "Active Submissions"}
                {selectedMetric === "interviews" && "Candidates in Interviews"}
                {selectedMetric === "offers" && "Open Offers Made"}
                {selectedMetric === "onboards" && "Onboarding in Progress"}
              </h3>
              <button
                onClick={() => setSelectedMetric(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-x-auto">
              {selectedMetric === "jobs" ? (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Job Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Submissions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Posted
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {job.title}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.client ||
                            job.client_name ||
                            job.company ||
                            "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.location || "Remote"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            {job.status || "Open"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {
                            submissions.filter((s) => s.job_id === job.id)
                              .length
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.created_at
                            ? new Date(job.created_at).toLocaleDateString()
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Job
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Submitted Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Submitted By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredData.map((submission) => {
                      const job = jobs.find((j) => j.id === submission.job_id);
                      return (
                        <tr key={submission.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {submission.candidate_name || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.job_title || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {job?.client ||
                              job?.client_name ||
                              job?.company ||
                              "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {(submission.current_status || submission.status)
                                ?.replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                                "N/A"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.submitted_at
                              ? new Date(
                                  submission.submitted_at
                                ).toLocaleDateString()
                              : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {submission.submitted_by_name || "N/A"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {filteredData.length === 0 && (
              <div className="p-12 text-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Job Requests Tab - Memoized to prevent input focus loss
  const JobRequestsTab = React.useMemo(() => {
    const filteredJobs = jobs.filter((job) => {
      if (filters.client !== "all") {
        const client = clients.find((c) => c.id === filters.client);
        if (job.company !== (client?.company_name || client?.client_name))
          return false;
      }
      if (filters.status !== "all" && job.status !== filters.status)
        return false;
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        return (
          job.title?.toLowerCase().includes(search) ||
          job.company?.toLowerCase().includes(search)
        );
      }
      return true;
    });

    return (
      <div className="space-y-4">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={filters.searchTerm}
                onChange={(e) =>
                  setFilters({ ...filters, searchTerm: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <select
              value={filters.client}
              onChange={(e) =>
                setFilters({ ...filters, client: e.target.value })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name || c.client_name}
                </option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={filters.priority}
              onChange={(e) =>
                setFilters({ ...filters, priority: e.target.value })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Job Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Submissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Interviews
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Offers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredJobs.map((job) => {
                const jobSubmissions = submissions.filter(
                  (s) => s.job_id === job.id
                );
                const jobInterviews = jobSubmissions.filter((s) =>
                  [
                    "interview_scheduled",
                    "interview_scheduled",
                    "interview_scheduled",
                  ].includes(s.current_status)
                ).length;
                const jobOffers = jobSubmissions.filter((s) =>
                  ["offer_made", "offer_accepted"].includes(s.current_status)
                ).length;

                return (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{job.title}</p>
                        <p className="text-sm text-gray-500">
                          {job.employment_type || "Full-time"}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {job.company}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {job.location || "Remote"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(job.created_at)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          job.status === "open"
                            ? "bg-green-100 text-green-800"
                            : job.status === "on_hold"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {jobSubmissions.length}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {jobInterviews}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {jobOffers}
                    </td>
                    <td className="px-6 py-4">
                      <button className="text-orange-600 hover:text-orange-700">
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [jobs, clients, submissions, filters, formatDate]);

  // Submissions Tab - Memoized to prevent input focus loss
  const SubmissionsTab = React.useMemo(() => {
    // Filter submissions to only show jobs created by Sales Sam
    const samJobIds = new Set(jobs.map((j) => j.id));

    const filteredSubmissions = submissions.filter((sub) => {
      // Only show submissions for Sam's jobs
      if (!samJobIds.has(sub.job_id)) return false;

      if (filters.client !== "all") {
        const client = clients.find((c) => c.id === filters.client);
        if (sub.client_name !== (client?.company_name || client?.client_name))
          return false;
      }
      if (filters.searchTerm) {
        const search = filters.searchTerm.toLowerCase();
        return (
          sub.candidate_name?.toLowerCase().includes(search) ||
          sub.job_title?.toLowerCase().includes(search)
        );
      }
      return true;
    });

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search submissions..."
                value={filters.searchTerm}
                onChange={(e) =>
                  setFilters({ ...filters, searchTerm: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <select
              value={filters.client}
              onChange={(e) =>
                setFilters({ ...filters, client: e.target.value })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name || c.client_name}
                </option>
              ))}
            </select>
            <select
              value={filters.dateRange}
              onChange={(e) =>
                setFilters({ ...filters, dateRange: e.target.value })
              }
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="last_7_days">Last 7 Days</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_90_days">Last 90 Days</option>
            </select>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Candidate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Recruiter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Job Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Stage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredSubmissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {submission.candidate_name?.charAt(0) || "C"}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {submission.candidate_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {submission.candidate_email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {submission.recruiter_name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {submission.job_title}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {submission.client_name}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {submission.current_status.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(submission.submission_date)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="text-orange-600 hover:text-orange-700 flex items-center gap-1"
                        title="View Submission Details"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="text-sm">View</span>
                      </button>
                      <button
                        onClick={() => {
                          setEditingSubmission(submission);
                          setShowEditSubmissionModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        title="Edit Submission Status"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="text-sm">Edit Status</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSubmissions.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No submissions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }, [submissions, clients, filters, formatDate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <div className="h-12 w-12 bg-orange-600 rounded-full flex items-center justify-center text-white">
              <TrendingUp className="h-6 w-6" />
            </div>
            Sales Sam Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Manage client relationships and track hiring progress
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchAllData}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { key: "overview", label: "Overview", icon: Activity },
              { key: "jobs", label: "Job Management", icon: Briefcase },
              { key: "submissions", label: "Client Submissions", icon: Send },
              {
                key: "customers",
                label: "Customer Management",
                icon: Building2,
              },
              { key: "contacts", label: "Contacts", icon: UserCircle },
              { key: "companies", label: "Companies", icon: Building2 },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.key
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {loading && activeTab === "overview" ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === "overview" && <OverviewTab />}
              {activeTab === "customers" && <CustomerManagement />}
              {activeTab === "jobs" && <JobManagement />}
              {activeTab === "submissions" && SubmissionsTab}
              {activeTab === "contacts" && (
                <ContactsTab
                  onAddContact={() => setShowContactModal(true)}
                  refreshTrigger={contactsRefreshTrigger}
                />
              )}
              {activeTab === "companies" && (
                <CompaniesTab
                  onAddCompany={() => {
                    setSelectedCompany(null);
                    setShowCompanyModal(true);
                  }}
                  onEditCompany={(company) => {
                    setSelectedCompany(company);
                    setShowCompanyModal(true);
                  }}
                  refreshTrigger={companiesRefreshTrigger}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Submission Details Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">
                Submission Details
              </h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-white hover:text-gray-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Candidate Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-orange-600" />
                  Candidate Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Name:
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedSubmission.candidate_name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Email:
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedSubmission.candidate_email || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Job Info */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                  Job Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Job Title:
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedSubmission.job_title}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Client:
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedSubmission.client_name || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Recruiter:
                    </span>
                    <span className="text-sm text-gray-900">
                      {selectedSubmission.recruiter_name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Submission Status */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-orange-600" />
                  Status
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Current Status:
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {selectedSubmission.current_status?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Submission Date:
                    </span>
                    <span className="text-sm text-gray-900">
                      {formatDate(selectedSubmission.submission_date)}
                    </span>
                  </div>
                  {selectedSubmission.match_score && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Match Score:
                      </span>
                      <span className="text-sm text-gray-900">
                        {Math.round(selectedSubmission.match_score * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status History */}
              {selectedSubmission.status_history &&
                selectedSubmission.status_history.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-orange-600" />
                      Status History
                    </h3>
                    <div className="space-y-2">
                      {selectedSubmission.status_history.map((history, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-50 rounded-lg p-3 text-sm"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium text-gray-900">
                                {history.status?.replace(/_/g, " ")}
                              </span>
                              {history.notes && (
                                <p className="text-gray-600 text-xs mt-1">
                                  {history.notes}
                                </p>
                              )}
                            </div>
                            <span className="text-gray-500 text-xs">
                              {formatDate(history.timestamp)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setSelectedSubmission(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Submission Edit Modal */}
      {showEditSubmissionModal && editingSubmission && (
        <ClientSubmissionEditModal
          isOpen={showEditSubmissionModal}
          onClose={() => {
            setShowEditSubmissionModal(false);
            setEditingSubmission(null);
          }}
          submission={editingSubmission}
          onUpdate={(updatedSubmission) => {
            fetchSubmissions();
            setShowEditSubmissionModal(false);
            setEditingSubmission(null);
            toast.success("Submission status updated successfully");
          }}
        />
      )}

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSuccess={handleContactCreated}
      />

      {/* Company Form Modal */}
      <CompanyCreateForm
        isOpen={showCompanyModal}
        onClose={() => {
          setShowCompanyModal(false);
          setSelectedCompany(null);
        }}
        onSuccess={handleCompanyCreated}
        company={selectedCompany}
      />
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, trend, subtitle }) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600",
    purple: "from-purple-500 to-purple-600",
    indigo: "from-indigo-500 to-indigo-600",
    green: "from-green-500 to-green-600",
    orange: "from-orange-500 to-orange-600",
    teal: "from-teal-500 to-teal-600",
    yellow: "from-yellow-500 to-yellow-600",
    pink: "from-pink-500 to-pink-600",
  };

  return (
    <div
      className={`bg-gradient-to-br ${colorClasses[color]} rounded-lg p-6 text-white`}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-white text-opacity-90 text-sm font-medium">
          {title}
        </p>
        <Icon className="h-8 w-8 text-white text-opacity-80" />
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && (
        <p className="text-white text-opacity-75 text-xs mt-1">{subtitle}</p>
      )}
      {trend && (
        <p className="text-white text-opacity-90 text-sm mt-2">{trend}</p>
      )}
    </div>
  );
};

export default SalesSamDashboard;
